// convertToLonLat.js
//
// Client-side module to convert point cloud centroids from their file CRS
// to WGS84 (EPSG:4326) longitude/latitude using proj4.
//
// Design notes:
// -------------
// - EPSG codes are preferred over raw WKT because proj4 definitions are
//   well-tested and standardized. WKT parsing is complex and error-prone,
//   especially for compound CRS or non-standard WKT variants.
//
// - Only horizontal CRS (2D) is handled. Vertical CRS (ellipsoidal height,
//   geoid models) is ignored because:
//   1) Most web mapping uses 2D lon/lat for positioning
//   2) Vertical datum transformations require geoid grids not bundled here
//   3) Point cloud Z values are typically kept in their original vertical CRS
//
// - Japan JGD2011 plane-rectangular zones are pre-registered because:
//   1) This application primarily handles Japanese survey data
//   2) These projections are not always available in proj4's defaults
//   3) Pre-registering avoids runtime network calls for definitions
//
// Limitations:
// - A CRS identifier is required; unregistered EPSG codes use WKT as a fallback
// - No support for dynamic definition fetching (browser-safe, offline-capable)

import proj4 from 'proj4';
import { extractEpsgFromWkt } from './extractEpsgFromWkt.js';
import { JGD2011_ZONES } from './jgd2011Zones.js';

export { extractEpsgFromWkt } from './extractEpsgFromWkt.js';

/**
 * Error codes returned by convertToLonLat
 * @readonly
 * @enum {string}
 */
export const ErrorCodes = {
  /** No EPSG code provided and none could be extracted from WKT */
  MISSING_CRS: 'MISSING_CRS',
  /** EPSG code provided but no proj4 definition is registered for it */
  UNKNOWN_EPSG: 'UNKNOWN_EPSG',
  /** proj4 transformation failed (e.g., coordinates outside valid range) */
  PROJ4_ERROR: 'PROJ4_ERROR',
  /** Input coordinates are not valid finite numbers */
  INVALID_COORDINATES: 'INVALID_COORDINATES',
};

/**
 * Additional commonly used projections.
 * Add more definitions here as needed.
 */
const ADDITIONAL_DEFS = {
  // WGS84 UTM zones (examples)
  // 32654: '+proj=utm +zone=54 +datum=WGS84 +units=m +no_defs', // UTM 54N
};

// Japanese plane rectangular coordinates use X=Northing, Y=Easting;
// proj4 expects [Easting, Northing]. Keep this set aligned with the zone table.
const NORTHING_EASTING_AXIS_ORDER = new Set(JGD2011_ZONES.map(zone => zone.code));

/** Track whether definitions have been registered */
let defsRegistered = false;

/**
 * Register Japan JGD2011 plane-rectangular zone definitions with proj4.
 * This function is idempotent - safe to call multiple times.
 */
function registerJapanDefs() {
  JGD2011_ZONES.forEach(({ code, definition }) => {
    proj4.defs(`EPSG:${code}`, definition);
  });
}

/**
 * Register additional projection definitions with proj4.
 */
function registerAdditionalDefs() {
  Object.entries(ADDITIONAL_DEFS).forEach(([epsg, def]) => {
    proj4.defs(`EPSG:${epsg}`, def);
  });
}

/**
 * Ensure all projection definitions are registered.
 * Called automatically before transformations.
 */
function ensureDefsRegistered() {
  if (!defsRegistered) {
    registerJapanDefs();
    registerAdditionalDefs();
    defsRegistered = true;
  }
}

/**
 * Check if a proj4 definition exists for the given EPSG code.
 *
 * @param {number} epsg - EPSG code to check
 * @returns {boolean} true if definition exists
 */
function hasProjection(epsg) {
  ensureDefsRegistered();
  try {
    const def = proj4.defs(`EPSG:${epsg}`);
    return def !== undefined;
  } catch {
    return false;
  }
}

/**
 * @typedef {Object} ConvertOptions
 * @property {number} x - X coordinate from file (may be Northing for Japanese CRS)
 * @property {number} y - Y coordinate from file (may be Easting for Japanese CRS)
 * @property {number} [epsg] - EPSG code of source CRS (preferred)
 * @property {string} [wkt] - WKT string of source CRS (fallback for EPSG extraction)
 *
 * Note on axis order:
 * - Most CRS use X=Easting, Y=Northing
 * - Japanese plane rectangular CRS (EPSG:6669-6687) use X=Northing, Y=Easting
 * - This function automatically handles the axis swap based on EPSG code
 * - Pass coordinates exactly as they appear in the LAS/LAZ header
 */

/**
 * @typedef {Object} ConvertSuccess
 * @property {true} ok - Indicates successful conversion
 * @property {number} lon - Longitude in degrees (WGS84)
 * @property {number} lat - Latitude in degrees (WGS84)
 * @property {number} epsgUsed - EPSG code that was used for conversion
 * @property {boolean} [axisSwapped] - True if coordinates were swapped due to Northing/Easting axis order
 */

/**
 * @typedef {Object} ConvertError
 * @property {false} ok - Indicates failed conversion
 * @property {{ code: string, message: string }} error - Error details
 */

/**
 * Convert coordinates from a source CRS to WGS84 (EPSG:4326).
 *
 * The function attempts to determine the source CRS in this order:
 * 1. Use provided EPSG code directly
 * 2. Extract EPSG code from provided WKT string
 * 3. Return MISSING_CRS error if neither is available
 *
 * @param {ConvertOptions} options - Conversion options
 * @returns {ConvertSuccess|ConvertError} Result object
 *
 * @example
 * // With EPSG code
 * const result = convertToLonLat({ x: 33497.96, y: 22081.84, epsg: 6669 });
 * if (result.ok) {
 *   console.log(result.lon, result.lat);
 * }
 *
 * @example
 * // With WKT (EPSG extracted automatically)
 * const result = convertToLonLat({ x: 33497.96, y: 22081.84, wkt: 'PROJCRS[...]' });
 */
export function convertToLonLat(options) {
  const { x, y, epsg, wkt } = options || {};

  // Validate coordinates
  if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.INVALID_COORDINATES,
        message: `Invalid coordinates: x=${x}, y=${y}. Both must be finite numbers.`,
      },
    };
  }

  // Determine EPSG code to use
  let epsgToUse = null;

  if (typeof epsg === 'number' && Number.isFinite(epsg) && epsg > 0) {
    epsgToUse = epsg;
  } else if (wkt) {
    epsgToUse = extractEpsgFromWkt(wkt);
  }

  // No CRS available
  if (epsgToUse === null) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.MISSING_CRS,
        message: 'No coordinate reference system provided. Please specify an EPSG code.',
      },
    };
  }

  // Determine the proj4 source definition to use
  let sourceDef;
  const hasRegistered = hasProjection(epsgToUse);

  if (hasRegistered) {
    sourceDef = `EPSG:${epsgToUse}`;
  } else if (wkt) {
    // Fall back to using the raw WKT string as a proj4 source definition.
    // proj4 v2.x can parse WKT1 and WKT2 strings directly.
    sourceDef = wkt;
  } else {
    return {
      ok: false,
      error: {
        code: ErrorCodes.UNKNOWN_EPSG,
        message: `Unknown EPSG code: ${epsgToUse}. No proj4 definition is registered for this CRS.`,
      },
    };
  }

  // Perform transformation
  try {
    ensureDefsRegistered();

    // Handle axis order: Japanese plane rectangular systems use X=Northing, Y=Easting
    // but proj4 expects [Easting, Northing], so we swap if needed.
    // Only apply axis swap for known Northing/Easting CRS (registered EPSG codes).
    // For WKT-based definitions, proj4 handles axis order internally.
    let inputCoords;
    if (hasRegistered && NORTHING_EASTING_AXIS_ORDER.has(epsgToUse)) {
      // Input: x=Northing, y=Easting → proj4 needs [Easting, Northing] = [y, x]
      inputCoords = [y, x];
    } else {
      // Standard axis order: x=Easting, y=Northing
      inputCoords = [x, y];
    }

    const result = proj4(sourceDef, 'EPSG:4326', inputCoords);

    // Validate result
    if (!Array.isArray(result) || result.length < 2 ||
        !Number.isFinite(result[0]) || !Number.isFinite(result[1])) {
      return {
        ok: false,
        error: {
          code: ErrorCodes.PROJ4_ERROR,
          message: `Transformation produced invalid result for coordinates (${x}, ${y}).`,
        },
      };
    }

    return {
      ok: true,
      lon: result[0],
      lat: result[1],
      epsgUsed: epsgToUse,
      axisSwapped: NORTHING_EASTING_AXIS_ORDER.has(epsgToUse),
    };
  } catch (err) {
    return {
      ok: false,
      error: {
        code: ErrorCodes.PROJ4_ERROR,
        message: `proj4 transformation failed: ${err.message || String(err)}`,
      },
    };
  }
}

/**
 * Get list of registered EPSG codes.
 * Useful for UI dropdowns or debugging.
 *
 * @returns {number[]} Array of registered EPSG codes
 */
export function getRegisteredEpsgCodes() {
  return [
    ...JGD2011_ZONES.map(zone => zone.code),
    ...Object.keys(ADDITIONAL_DEFS).map(Number),
  ];
}

/**
 * Manually register a custom projection definition.
 *
 * @param {number} epsg - EPSG code
 * @param {string} proj4Def - proj4 definition string
 */
export function registerProjection(epsg, proj4Def) {
  proj4.defs(`EPSG:${epsg}`, proj4Def);
}

// ============================================================================
// Example usage:
// ============================================================================
//
// import { convertToLonLat } from './convertToLonLat';
// import { readLazHeaderInfo } from './lazHeaderReader';
//
// async function processLazFile(file) {
//   const header = await readLazHeaderInfo(file);
//
//   const r = convertToLonLat({
//     x: header.center[0],
//     y: header.center[1],
//     epsg: header.epsg,
//     wkt: header.wkt
//   });
//
//   if (r.ok) {
//     console.log(`Center: ${r.lon}, ${r.lat} (using EPSG:${r.epsgUsed})`);
//     return { lon: r.lon, lat: r.lat };
//   }
//
//   if (r.error.code === 'MISSING_CRS') {
//     // Ask user to select EPSG from dropdown
//     const userEpsg = await askUserForEpsg();
//     return convertToLonLat({
//       x: header.center[0],
//       y: header.center[1],
//       epsg: userEpsg
//     });
//   }
//
//   // Handle other errors
//   console.error(r.error.message);
//   return null;
// }
